import { Component, OnInit, Input, Output,EventEmitter, OnChanges, OnDestroy, SimpleChanges, ViewChildren, QueryList } from '@angular/core';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';

import { AppSect, NoQuestionSection } from '../../model/TemplateRulebook';
import { TemplateService } from '../../service/template.service';
import { ParagraphChecklistComponent } from './paragraph-checklist/paragraph-checklist.component';

// One row of the merged, section-number-ordered section list rendered in the template - either
// a section with an applicability question (ac set) or one without (ac null, header-only).
export interface MergedSection {
  rbSectNum: number;
  rbSectKey: number;
  rbSectName: string;
  ac: AppSect | null;
}

@Component({
    selector: 'app-answer-question',
    templateUrl: './answer-question.component.html',
    styleUrls: ['./answer-question.component.css'],
    standalone: false
})
export class AnswerQuestionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() appSect$: Observable<AppSect[]>;
  // Sections with no applicability question at all - rendered header-only, independent of
  // the acs loop below, since there's no question/answer to drive for them.
  @Input() noQuestionSections$: Observable<NoQuestionSection[]>;
  @Input() rbName: string;
  @Input() editable: boolean;
  @Input() csskey: number;
  @Input() appkey: number;
  @Input() showSecondLevelScreening: boolean;
  @Output() answerApp = new EventEmitter<string>();
  @Output() viewSigChange = new EventEmitter<AppSect>();
  @Output() sigResolved = new EventEmitter<boolean>();

  // Only the sections for the currently-selected rulebook are ever rendered, so this only
  // covers what's on screen right now - it's a fast client-side pre-check, not a substitute
  // for the backend's own (template-wide) check before actually turning screening off.
  @ViewChildren(ParagraphChecklistComponent) paragraphChecklists: QueryList<ParagraphChecklistComponent>;

  appSects: BehaviorSubject<AppSect[]>;
  isLoading = false;
  // Collapsed by default so the instructions don't take up space once a user already knows them.
  introExpanded = false;

  // appSect$ is a cold HTTP observable - each subscribe() fires a fresh GetApplicableQuestions
  // request. Tracked here so a re-subscribe (only when appSect$ itself changes, see
  // ngOnChanges) first unsubscribes the previous one, instead of leaving it running alongside
  // the new one.
  private appSectSubscription?: Subscription;

  constructor(private service: TemplateService) {
    this.appSects = new BehaviorSubject(null);
   }

  toggleIntro() {
    this.introExpanded = !this.introExpanded;
  }

  // Whether any currently-rendered paragraph checklist (for the rulebook the user is on
  // right now) has an excluded paragraph.
  hasExcludedParagraphs(): boolean {
    return (this.paragraphChecklists?.toArray() ?? []).some((pc) =>
      pc.hasAnyUnchecked()
    );
  }

  // Combine the section-question items (from acs, excluding the rulebook-level item at
  // rbSectNum 0) with the no-question sections into one section-number-ordered list, so they
  // render as a single sequence instead of two separate groups. No-question sections are only
  // included while Detailed Requirements Screening is on - they exist for screening only.
  mergeSections(
    acs: AppSect[],
    noQuestionSections: NoQuestionSection[]
  ): MergedSection[] {
    const questioned: MergedSection[] = acs
      .filter((ac) => ac.rbSectNum > 0)
      .map((ac) => ({
        rbSectNum: ac.rbSectNum,
        rbSectKey: ac.rbSectKey,
        rbSectName: ac.rbSectName,
        ac,
      }));
    const plain: MergedSection[] = this.showSecondLevelScreening
      ? (noQuestionSections ?? []).map((nqs) => ({
          rbSectNum: nqs.rbSectNum,
          rbSectKey: nqs.rbSectKey,
          rbSectName: nqs.rbSectName,
          ac: null,
        }))
      : [];
    return [...questioned, ...plain].sort((a, b) => a.rbSectNum - b.rbSectNum);
  }

  // to get the @input data from parent.
  ngOnInit() {
    this.subscribeToAppSect();
    this.service.isLoading$.subscribe(data =>{
      this.isLoading = data;
    });
  }

  // Re-subscribe only when appSect$ itself was reassigned (a different rulebook was picked -
  // see EditApplicabilityComponent.loadApplicability), not on every @Input change. Toggling
  // showSecondLevelScreening also runs ngOnChanges (it's an @Input too), and previously this
  // re-subscribed to appSect$ unconditionally and without unsubscribing the prior subscription -
  // each toggle click left yet another live GetApplicableQuestions request running, and repeated
  // toggling piled up enough of them to freeze the page.
  ngOnChanges(changes: SimpleChanges) {
    if (changes['appSect$'] && !changes['appSect$'].firstChange) {
      this.subscribeToAppSect();
    }
  }

  ngOnDestroy() {
    this.appSectSubscription?.unsubscribe();
  }

  private subscribeToAppSect() {
    this.appSectSubscription?.unsubscribe();
    this.appSectSubscription = this.appSect$.subscribe(data => {
      this.appSects.next(data);
    });
  }

  getAppSects(){
    return this.appSects;
  }

  setAppSects(aps: AppSect[]){
    this.appSects.next(aps);
  }

  getAnswer(){
    var answer = "";
    this.getAppSects().value.forEach( x => {
      answer += x.answerQ;
      if(x.answerE !== "0"){
        answer += x.answerE;
      }
    });
    return answer;
  }

  // Answer will be one of Y/N/I
  answerToAll(answer: string){
    //console.log("answerToAll(): answer = " + answer);
    this.appSects.value.forEach( v => {
      if(v.rbSectNum > 0){ // Skip the applicability question for rulebook
        v.answerQ = answer;
        v.sigChangeUpdate = false;
        //console.log("set for " + v.rbSectNum + " to " + answer);
        if(v.answerE != "0"){
          v.answerE = this.resetExemptionAnswer(answer);
        }
        this.clearParaExclusionsIfNotApplicable(v, answer, v.answerE);
      }
    });

    this.setAppSects(this.appSects.value);
    //this.appSect$ = this.getAppSects();
    this.isLoading = true;
    this.answerApp.emit(this.getAnswer());
    this.emitSigResolved();
  }

  //
  resetExemptionAnswer(answer: string){
    if(answer == "Y")
      return "n";
    return "i";
  }

  // Answer rulebook applicable question
  answerRbAppQ(index: number, answer: string){
    const ac = this.appSects.value[index];
    ac.answerQ = answer;
    ac.sigChangeUpdate = false;
    this.clearParaExclusionsIfNotApplicable(ac, answer, ac.answerE);
    this.setAppSects(this.appSects.value);
    this.appSect$ = this.getAppSects();
    this.isLoading = true;
    this.answerApp.emit(this.getAnswer());
    this.emitSigResolved();
  }

  // A section stops being applicable - and its saved paragraph exclusions are no longer
  // meaningful - either by answering the section question "No", or by answering "Yes" to its
  // exemption (an exemption exempts the customer the same as a "No" would). Delete whatever
  // was excluded so re-reaching "Yes" (no exemption) later starts the checklist fresh instead
  // of restoring stale exclusions.
  clearParaExclusionsIfNotApplicable(ac: AppSect, answerQ: string, answerE: string){
    const exempted = answerE != null && answerE.toLocaleString() == 'y';
    if((answerQ === 'N' || exempted) && ac.rbSectNum > 0 && ac.rbSectKey > 0){
      this.service.clearParaExclusions(this.csskey, this.appkey, ac.rbSectKey).subscribe();
    }
  }

  answerRbAppE(index: number, answer: string){
    //console.log(index + ": " + answer);
    const ac = this.appSects.value[index];
    ac.answerE = answer;
    ac.sigChangeUpdate = false;
    this.clearParaExclusionsIfNotApplicable(ac, ac.answerQ, answer);
    this.setAppSects(this.appSects.value);
    this.appSect$ = this.getAppSects();
    this.isLoading = true;
    this.answerApp.emit(this.getAnswer());
    this.emitSigResolved();
  }

  // Notify the parent that the customer wants to see what changed for this applicability question.
  onViewSigChange(ac: AppSect, event: Event){
    event.stopPropagation();
    this.viewSigChange.emit(ac);
  }

  // Tell the parent whether every significant-change flag on this rulebook's
  // applicability questions/exemptions has now been answered, so it knows
  // when it's safe to clear the rulebook's 'Needs Review' status.
  emitSigResolved(){
    this.sigResolved.emit(this.appSects.value.every(a => !a.sigChangeUpdate));
  }
}